import { ApolloCache, gql } from '@apollo/client';
import isEmpty from 'lodash/isEmpty';
import React from 'react';

import { useDashboardDataContext } from '../components/project/dashboard/dashboard-context';

import { useAssessmentProgressOptional } from '~/client/components/assessment/assessment-progress-context';
import { useNotificationContextOptional } from '~/client/contexts/notification-context';
import { createAnswerStatusKey } from '~/client/hooks/use-answer-status-sync';
import * as GQL from '~/graphql/generated/client';
import { useSnackbarContext } from '~c/components/base/snackbar';

/**
 *
 * @param cache - Apollo cache instance
 * @param assessmentId - The assessment being updated
 * @param controlBundleId - The control bundle containing the answer
 * @param controlIdentifier - The specific control being answered
 * @param values - New answer values (for value updates)
 * @param cards - New card data (for card operations)
 * @param cardOperation - Type of card operation (Add, Update, Delete)
 * @param answerStatus - The answer status (CONFIGURED, UNCONFIGURED, DRAFT)
 */
function updateAnswersInCache(
  cache: ApolloCache<unknown>,
  assessmentId: string,
  controlBundleId: string,
  controlIdentifier: string,
  values?: GQL.AnswerValueInput[],
  cards?: GQL.Card[],
  cardOperation?: GQL.CardOperation,
  answerStatus?: GQL.AnswerStatus | null
) {
  // Read the full assessment from cache to find the answerBundle
  const assessmentCacheId = cache.identify({
    __typename: 'Assessment',
    _id: assessmentId,
  });

  if (!assessmentCacheId) {
    return;
  }

  const assessmentData = cache.readFragment<{
    answerBundles: { _id: string; controlBundleId: string }[];
  }>({
    id: assessmentCacheId,
    fragment: gql`
      fragment AssessmentBundles on Assessment {
        answerBundles {
          _id
          controlBundleId
        }
      }
    `,
  });

  if (!assessmentData?.answerBundles) {
    return;
  }

  // Find the answerBundle that matches our controlBundleId
  const targetBundle = assessmentData.answerBundles.find(
    (bundle) => bundle.controlBundleId === controlBundleId
  );

  if (!targetBundle) {
    return;
  }

  const bundleCacheId = cache.identify({
    __typename: 'AnswerBundle',
    _id: targetBundle._id,
  });

  if (!bundleCacheId) {
    return;
  }

  // Modify the answers array within the target bundle
  cache.modify({
    id: bundleCacheId,
    fields: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      answers(existingAnswersOrRef: any, { readField }) {
        // Handle case where answers might be a reference or undefined
        const existingAnswers: readonly any[] = Array.isArray(
          existingAnswersOrRef
        )
          ? existingAnswersOrRef
          : [];

        // Find existing answer for this control
        const existingAnswerIndex = existingAnswers.findIndex(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (answerRef: any) => {
            const answerControlId = readField('controlIdentifier', answerRef);
            return answerControlId === controlIdentifier;
          }
        );

        // Handle card operations
        if (cardOperation && cards !== undefined) {
          if (existingAnswerIndex === -1) {
            // No existing answer, create new one with cards
            if (cardOperation === GQL.CardOperation.Add && cards.length > 0) {
              return [
                ...existingAnswers,
                {
                  __typename: 'Answer',
                  controlBundleId,
                  controlIdentifier,
                  values: [],
                  cards: cards.map((card) => ({
                    __typename: 'Card',
                    ...card,
                  })),
                  answerStatus: answerStatus ?? null,
                },
              ];
            }
            return existingAnswers;
          }

          // Update existing answer's cards
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          return existingAnswers.map((answer: any, idx: number) => {
            if (idx !== existingAnswerIndex) {
              return answer;
            }

            const existingCards =
              (readField('cards', answer) as GQL.Card[]) ?? [];

            let updatedCards: GQL.Card[];
            switch (cardOperation) {
              case GQL.CardOperation.Add:
                updatedCards = [
                  ...existingCards,
                  ...cards.map((card) => ({
                    __typename: 'Card' as const,
                    ...card,
                  })),
                ];
                break;
              case GQL.CardOperation.Update:
                updatedCards = existingCards.map((existing) => {
                  const updated = cards.find((c) => c._id === existing._id);
                  return updated
                    ? { __typename: 'Card' as const, ...updated }
                    : existing;
                });
                break;
              case GQL.CardOperation.Delete: {
                const deleteIds = new Set(cards.map((c) => c._id));
                updatedCards = existingCards.filter(
                  (c) => !deleteIds.has(c._id)
                );
                break;
              }
              default:
                updatedCards = existingCards;
            }

            // Merge with existing answer data
            return {
              __typename: 'Answer',
              controlBundleId: readField('controlBundleId', answer),
              controlIdentifier: readField('controlIdentifier', answer),
              values: readField('values', answer) ?? [],
              cards: updatedCards,
              answerStatus:
                answerStatus ?? readField('answerStatus', answer) ?? null,
            };
          });
        }

        // Handle value updates
        if (values !== undefined) {
          const newAnswerData = {
            __typename: 'Answer' as const,
            controlBundleId,
            controlIdentifier,
            values: values.map((v) => ({
              __typename: 'AnswerValue' as const,
              answerOptionId: v.answerOptionId,
              answerOptionIdentifier: v.answerOptionIdentifier ?? null,
              value: v.value,
              extraValue: v.extraValue ?? null,
              labels: v.labels ?? null,
              evidence: v.evidence?.map((e) => ({
                __typename: 'Evidence' as const,
                ...e,
              })),
              comment: v.comment ?? null,
            })),
            cards: [] as GQL.Card[],
            answerStatus: answerStatus ?? null,
          };

          if (existingAnswerIndex === -1) {
            // Add new answer
            return [...existingAnswers, newAnswerData];
          }

          // Update existing answer - preserve cards, update values
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          return existingAnswers.map((answer: any, idx: number) => {
            if (idx !== existingAnswerIndex) {
              return answer;
            }
            const existingCards =
              (readField('cards', answer) as GQL.Card[]) ?? [];
            return {
              ...newAnswerData,
              cards: existingCards,
            };
          });
        }

        return existingAnswers;
      },
    },
  });
}

/**
 * Updates bundle metrics (completionPercent, numAnswers) in Apollo cache.
 * Called after mutation returns the updated bundle data.
 */
function updateBundleMetricsInCache(
  cache: ApolloCache<unknown>,
  updatedBundle: {
    _id: string;
    controlBundleId: string;
    completionPercent?: number | null;
    numAnswers?: number | null;
    numControls?: number | null;
  }
) {
  if (!updatedBundle._id) {
    return;
  }

  const cacheId = cache.identify({
    __typename: 'AnswerBundle',
    _id: updatedBundle._id,
  });

  if (!cacheId) {
    return;
  }

  cache.modify({
    id: cacheId,
    fields: {
      completionPercent: () => updatedBundle.completionPercent,
      numAnswers: () => updatedBundle.numAnswers,
      numControls: () => updatedBundle.numControls,
    },
  });
}

function updateAssessmentInCache(
  cache: ApolloCache<unknown>,
  assessmentSummary: {
    _id: string;
    completionPercent?: number | null;
    status: GQL.AssessmentStatusType;
    updatedAt?: number | Date | null;
  }
) {
  const cacheId = cache.identify({
    __typename: 'Assessment',
    _id: assessmentSummary._id,
  });

  if (!cacheId) {
    return;
  }

  cache.modify({
    id: cacheId,
    fields: {
      completionPercent: () => assessmentSummary.completionPercent,
      status: () => assessmentSummary.status,
      updatedAt: () => assessmentSummary.updatedAt,
    },
  });
}

/**
 * Form helper for:
 * - getting value of a question in control bundle
 * - setting value of a question in control bundle
 */
export interface InputCommonProps {
  control: GQL.ControlFragment;
  readOnly?: boolean;
  disabled?: boolean;
  values: GQL.AnswerValue[];
  cards: GQL.Card[];
  answerStatus?: GQL.AnswerStatus | null;
  /** Control-level assignment (question delegation) if any */
  assignment?: GQL.ControlAssignment | null;
  onValueChange: (
    values: GQL.AnswerValueInput[],
    answerStatus?: GQL.AnswerStatus | null
  ) => void;
  onCardOperation: (
    operation: GQL.CardOperation,
    card: GQL.Card,
    isPartialUpdate?: boolean
  ) => void;
}

export interface DisplayComment {
  email: string;
  userId: string;
  name: string;
  comment: string;
  sendAt: Date;
}

interface AnswerBundle {
  controlBundleId: string;
  _id: string;
}

interface OrganizedData {
  [key: string]: GQL.AnswerBundle[];
}

/**
 * Helper hook for retrieving an assessment object given an assessment ID
 */
export function useAssessmentFromId(projectId: ID, assessmentId: ID) {
  const Snackbar = useSnackbarContext();
  const progressContext = useAssessmentProgressOptional();
  const notificationContext = useNotificationContextOptional();

  // Refetch notification count after mutations that might trigger risk alerts
  // Use a small delay since the server creates notifications asynchronously
  const refetchNotificationsAfterDelay = React.useCallback(() => {
    if (notificationContext?.refetchUnreadCount) {
      setTimeout(() => {
        notificationContext.refetchUnreadCount();
      }, 1000); // 1 second delay for server to create notification
    }
  }, [notificationContext]);

  const [updateAnswer, updateAnswerResult] =
    GQL.useAssessmentUpdateAnswerMutation({
      // Refetch the dashboard query by name so the project cards / pies
      // pick up the cachedMetrics the server just recomputed inside
      // `awaitFastRecomputeAndTriggerDeep`. Apollo refetches with the
      // active variables, so we don't need to hard-code the project id.
      refetchQueries: ['ProjectForDashboard'],
      onCompleted: () => {
        // Refetch notifications - a risk alert may have been created
        refetchNotificationsAfterDelay();
      },
    });
  const [updateCard, updateCardResult] =
    GQL.useAssessmentUpdateCardAnswerMutation({
      refetchQueries: [
        {
          query: GQL.GetAssessmentVersionsDocument,
          variables: { assessmentId },
        },
        // Same reason as updateAnswer — pull the recomputed cachedMetrics
        // back into the dashboard cache.
        'ProjectForDashboard',
      ],
      onCompleted: () => {
        // Refetch notifications - a risk alert may have been created
        refetchNotificationsAfterDelay();
      },
    });
  const {
    project: data,
    refetchProjectFromId,
    ...projectResult
  } = useDashboardDataContext();
  // Expose dashboard refetch as refetch so callers (e.g. assessment landing) refresh assessment data
  const refetch = refetchProjectFromId;

  React.useEffect(() => {
    if (projectResult.error) {
      Snackbar.open(projectResult.error);
    }
    if (updateAnswerResult.error) {
      Snackbar.open(updateAnswerResult.error);
    }
    if (updateCardResult.error) {
      Snackbar.open(updateCardResult.error);
    }
  }, [projectResult.error, updateAnswerResult.error, updateCardResult.error]);

  const assessmentList = data?.assessments ?? [];
  const assessmentFromDashboard = assessmentList.find(
    ({ _id }) => _id === assessmentId
  );

  // For versioned assessments, fetch full assessment (with answerBundles from latest version)
  // so left nav (0/17), report, and bundle form have real answers. Dashboard omits answerBundles.
  const latestVersionId = assessmentFromDashboard?.latestVersionId?.toString();
  const { data: versionData } = GQL.useAssessmentWithVersionQuery({
    variables: {
      assessmentId: assessmentId ?? '',
      versionId: latestVersionId ?? '',
    },
    skip:
      !assessmentId ||
      !assessmentFromDashboard?.enableVersioning ||
      !latestVersionId,
    fetchPolicy: 'cache-first',
  });

  const assessmentWithAnswers = versionData?.assessmentWithVersion;
  const assessment =
    assessmentWithAnswers && assessmentFromDashboard
      ? {
          ...assessmentFromDashboard,
          answerBundles: (assessmentWithAnswers as GQL.AssessmentFragment)
            .answerBundles,
        }
      : assessmentFromDashboard;

  // When we're on an assessment route but the assessment isn't in the context list yet
  // (e.g. direct load or cache from different project), treat as loading and refetch once
  const missingAssessment =
    assessmentId && data && !assessmentList.some((a) => a._id === assessmentId);
  const hasRefetchedForMissing = React.useRef(false);
  React.useEffect(() => {
    if (!missingAssessment || hasRefetchedForMissing.current) {
      return;
    }
    hasRefetchedForMissing.current = true;
    refetchProjectFromId?.();
  }, [missingAssessment, refetchProjectFromId]);

  const effectiveLoading =
    projectResult.loading || (!!assessmentId && !!data && !assessment);

  const bundleHelper = React.useCallback(
    (bundleId: string, versionId: string) => {
      return bundleHelperFactory({
        assessment,
        bundleId,
        updateAnswer,
        updateCard,
        Snackbar,
        versionId,
        progressContext,
      });
    },
    [assessment, updateAnswer, updateCard, Snackbar, progressContext]
  );

  return {
    assessment,
    bundleHelper,
    canEdit: canEditAssessment(assessment),
    canValidate: canSubmitForValidation(assessment),
    ...projectResult,
    refetch,
  } as const;
}

/**
 * Is user allowed to edit assessment?
 */
export function canEditAssessment(
  assessment?: GQL.AssessmentFragment | null
): assessment is GQL.AssessmentFragment {
  return (
    assessment?.status === GQL.AssessmentStatusType.NotStarted ||
    assessment?.status === GQL.AssessmentStatusType.InProgress
  );
}

/**
 * Can user submit the assessment for validation/review
 */
function canSubmitForValidation(
  assessment?: GQL.AssessmentFragment | null
): assessment is GQL.AssessmentFragment {
  const canEdit = canEditAssessment(assessment);
  return canEdit && assessment?.completionPercent === 100;
}

interface BunderHelperFactoryArg {
  assessment: GQL.AssessmentFragment | null | undefined;
  bundleId: string;
  updateAnswer: ReturnType<typeof GQL.useAssessmentUpdateAnswerMutation>[0];
  updateCard: ReturnType<typeof GQL.useAssessmentUpdateCardAnswerMutation>[0];
  Snackbar: ReturnType<typeof useSnackbarContext>;
  versionId?: string;
  progressContext: ReturnType<typeof useAssessmentProgressOptional>;
}

const organizedAssessmentByVersion = (answerBundle_: GQL.AnswerBundle[]) => {
  const organizedData: OrganizedData = {};

  answerBundle_?.forEach((item) => {
    const controlBundleId = item.controlBundleId;
    if (!organizedData[controlBundleId]) {
      organizedData[controlBundleId] = [];
    }
    if (item) {
      organizedData[controlBundleId].push(item as GQL.AnswerBundle);
    }
  });
  // Sort the AnswerBundle arrays by createdAt
  Object.values(organizedData).forEach((arr) => {
    arr.sort((a, b) => {
      //sort answer bundles by created date
      if (a.createdAt && b.createdAt) {
        return b.createdAt - a.createdAt;
      }
      return 0;
    });
  });
  return organizedData;
};

/**
 * Helper function for:
 * - retrieving a bundle object
 * - retrieving an answer bundle object
 * - updating an answer given an controlIdentifier and values
 */
function bundleHelperFactory(arg: BunderHelperFactoryArg) {
  const { assessment, bundleId, Snackbar, versionId } = arg;

  const controlBundleList = assessment?.policy?.controlBundleList ?? [];

  const bundleIdx = controlBundleList.findIndex(({ _id }) => _id === bundleId);
  const bundle = bundleIdx !== -1 ? controlBundleList[bundleIdx] : null;

  const controlBundleCount = controlBundleList.length;

  const answerBundleList = assessment?.answerBundles ?? [];

  const matchByBundleId = ({ controlBundleId }: AnswerBundle) =>
    controlBundleId === bundleId;

  // const answerBundleData = organizedAssessmentByVersion(
  //   answerBundleList as GQL.AnswerBundle[]
  // );
  // console.log(answerBundleData, bundleId);
  // const answerBundle = versionId?.length
  //   ? answerBundleData[bundleId]?.find(matchByIdAndVersion)
  //   : answerBundleData[bundleId]?.find(matchByBundleId);

  const answerBundle = answerBundleList.find(matchByBundleId);

  /**
   * Total number of questions for the control bundle (visibility-aware).
   * Prefers backend-computed numControls which excludes hidden controls,
   * falls back to raw control count when no answerBundle exists yet.
   */
  const controlCount =
    answerBundle?.numControls ?? bundle?.controls?.length ?? 0;

  /**
   * Total number of answers with non-empty values for this bundle.
   */
  const answerCount = answerBundle?.numAnswers ?? 0;

  /**
   * Bundle completion percentage from cache.
   * Used for progress bar display.
   */
  const bundleCompletionPercent = answerBundle?.completionPercent ?? 0;

  /**
   * Get answer values for a given controlIdentifier within the control bundle
   */
  const getAnswerValues = (controlIdentifier: string) => {
    const answer = (answerBundle?.answers ?? []).find(
      (entry) => controlIdentifier === entry.controlIdentifier
    );
    const values = (answer?.values ?? []).map<GQL.AnswerValueInput>(
      ({ __typename, ...others }) => ({
        ...others,
        // Also clean evidence if it exists
        evidence: others.evidence?.map((evidenceItem) => {
          if (!evidenceItem) {
            return evidenceItem;
          }
          const { __typename: evidenceTypename, ...evidenceRest } =
            evidenceItem;
          return evidenceRest;
        }),
      })
    );
    return values;
  };

  /**
   * Get cards for a given controlIdentifier within the control bundle
   */
  const getAnswerCards = (controlIdentifier: string) => {
    const answer = (answerBundle?.answers ?? []).find(
      (entry) => controlIdentifier === entry.controlIdentifier
    );
    return answer?.cards ?? [];
  };

  /**
   * Get the control-level assignment for a given controlIdentifier
   */
  const getAnswerAssignment = (
    controlIdentifier: string
  ): GQL.ControlAssignment | null | undefined => {
    const answer = (answerBundle?.answers ?? []).find(
      (entry) => controlIdentifier === entry.controlIdentifier
    );
    return answer?.assignment;
  };

  /**
   * Get answerStatus for a given controlIdentifier within the control bundle
   * Checks progress context first for live updates, then falls back to cached value
   */
  const getAnswerStatus = (
    controlIdentifier: string
  ): GQL.AnswerStatus | null | undefined => {
    // Check progress context first for instant UI updates
    // Use composite key scoped to assessment (prevents cross-project conflicts)
    const contextKey = assessment?._id
      ? createAnswerStatusKey(assessment._id, controlIdentifier)
      : null;

    if (contextKey) {
      const liveStatus = arg.progressContext?.getAnswerStatus(contextKey);
      if (liveStatus !== undefined) {
        return liveStatus;
      }
    }

    // Fall back to cached value
    const answer = (answerBundle?.answers ?? []).find(
      (entry) => controlIdentifier === entry.controlIdentifier
    );
    return answer?.answerStatus;
  };

  /**
   * Update the card for a given controlIdentifier within the control bundle
   */
  const onCardOperation = async (
    controlId: string,
    operation: GQL.CardOperation,
    card: {
      _id?: string;
      cardId: string;
      values: {
        cardFieldId: string;
        value: string;
        values?: (GQL.CardFieldAnswerValueInput | null)[];
      }[];
    },
    isPartialUpdate?: boolean
  ) => {
    const selectedVersionId = versionId?.length ? versionId : undefined;

    // Store previous status for rollback on failure
    const contextKey = assessment?._id
      ? createAnswerStatusKey(assessment._id, controlId)
      : null;
    const previousStatus = contextKey
      ? arg.progressContext?.getAnswerStatus(contextKey)
      : undefined;

    // Calculate the new answer status based on operation
    // ADD/UPDATE operations mean the control has content (configured)
    // DELETE: If deleting the last card, set to UNCONFIGURED
    let newAnswerStatus: GQL.AnswerStatus | undefined;
    if (
      operation === GQL.CardOperation.Add ||
      operation === GQL.CardOperation.Update
    ) {
      newAnswerStatus = GQL.AnswerStatus.Configured;
    } else if (operation === GQL.CardOperation.Delete) {
      const currentCards = getAnswerCards(controlId);
      const isLastCard = currentCards.length <= 1;
      newAnswerStatus = isLastCard ? GQL.AnswerStatus.Unconfigured : undefined;
    }

    // Update answer status in context IMMEDIATELY for instant UI feedback
    // Use composite key scoped to assessment (prevents cross-project conflicts)
    if (arg.progressContext && contextKey && newAnswerStatus !== undefined) {
      arg.progressContext.updateAnswerStatus(contextKey, newAnswerStatus);
    }

    // Abort controller with 30s timeout to prevent hanging requests (e.g. offline)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    try {
      const result = await arg.updateCard({
        variables: {
          input: {
            versionId: selectedVersionId,
            assessmentId: assessment?._id!,
            controlBundleId: bundleId,
            controlIdentifier: controlId,
            operation,
            card,
            isPartialUpdate,
          },
        },
        context: {
          fetchOptions: { signal: controller.signal },
        },
        // Manually update cache since mutation returns minimal response (Option C)
        update: (cache, { data }) => {
          // 1. Update the answers array in cache (cards and answerStatus)
          updateAnswersInCache(
            cache,
            assessment?._id!,
            bundleId,
            controlId,
            undefined, // no values update
            [card as GQL.Card], // card being operated on
            operation,
            newAnswerStatus // Update status (UNCONFIGURED when deleting last card)
          );

          // 2. Update bundle metrics from response
          const updatedBundle = data?.assessmentUpdateCardAnswer?.updatedBundle;
          if (updatedBundle) {
            updateBundleMetricsInCache(cache, updatedBundle);

            // Update progress context for live UI updates
            if (arg.progressContext) {
              arg.progressContext.updateBundleProgress(
                updatedBundle._id,
                updatedBundle.completionPercent ?? 0,
                updatedBundle.numAnswers ?? 0,
                updatedBundle.numControls ?? 0
              );
            }
          }

          // Note: Answer status context update is done BEFORE mutation (line 591-613)
          // to prevent race conditions with stale Apollo cache data

          // 3. Update assessment summary fields (completionPercent, status, updatedAt)
          const assessmentSummary =
            data?.assessmentUpdateCardAnswer?.assessment;
          if (assessmentSummary) {
            updateAssessmentInCache(cache, assessmentSummary);

            // Update progress context for live UI updates
            if (arg.progressContext && assessment?._id) {
              arg.progressContext.updateAssessmentCompletion(
                assessment._id,
                assessmentSummary.completionPercent ?? 0
              );
            }
          }
        },
      });
      clearTimeout(timeoutId);
      return result;
    } catch (err: any) {
      clearTimeout(timeoutId);
      // Rollback context update on mutation failure
      if (arg.progressContext && contextKey) {
        arg.progressContext.updateAnswerStatus(
          contextKey,
          previousStatus as GQL.AnswerStatus | null
        );
      }
      if (controller.signal.aborted) {
        Snackbar.open(
          'Card operation timed out. Please check your connection and try again.'
        );
      } else {
        Snackbar.open(err);
      }
      throw err; // Re-throw to let components handle UI rollback if needed
    }
  };

  /**
   * Update the answer for a given controlIdentifier within the control bundle
   */
  const onValueChange = async (
    controlId: string,
    values: GQL.AnswerValueInput[],
    answerStatus?: GQL.AnswerStatus | null
  ) => {
    const selectedVersionId = versionId?.length ? versionId : undefined;

    // Mapping values with adjusted properties
    const mappedValues = values.map((entry) => ({
      ...entry,
      value: isEmpty(entry.value) ? null : entry.value,
      evidence: entry.evidence ?? null,
    }));

    // Store previous status for rollback on failure
    const contextKey = assessment?._id
      ? createAnswerStatusKey(assessment._id, controlId)
      : null;
    const previousStatus = contextKey
      ? arg.progressContext?.getAnswerStatus(contextKey)
      : undefined;

    // Update answer status in context IMMEDIATELY for instant UI feedback
    // This prevents race conditions where stale Apollo cache data overwrites local state
    // Context update happens BEFORE mutation, not in the update callback
    // Use composite key scoped to assessment (prevents cross-project conflicts)
    if (arg.progressContext && answerStatus !== undefined && contextKey) {
      arg.progressContext.updateAnswerStatus(contextKey, answerStatus);
    }

    // Capture answerBundleId for optimistic progress rollback on error.
    // answerBundle is in closure scope from bundleHelperFactory.
    const answerBundleId = answerBundle?._id;

    try {
      return await arg.updateAnswer({
        variables: {
          input: {
            versionId: selectedVersionId,
            assessmentId: assessment?._id!,
            controlBundleId: bundleId,
            controlIdentifier: controlId,
            values: mappedValues,
            answerStatus,
          },
        },
        // Manually update cache since mutation returns minimal response (Option C)
        update: (cache, { data }) => {
          // 1. Update the answers array in cache (including answerStatus)
          updateAnswersInCache(
            cache,
            assessment?._id!,
            bundleId,
            controlId,
            mappedValues, // the values being saved
            undefined, // no card operation
            undefined,
            answerStatus // pass answerStatus to cache update
          );

          // 2. Update bundle metrics from response
          const updatedBundle = data?.assessmentUpdateAnswer?.updatedBundle;
          if (updatedBundle) {
            updateBundleMetricsInCache(cache, updatedBundle);

            // Update progress context for live UI updates
            if (arg.progressContext) {
              arg.progressContext.updateBundleProgress(
                updatedBundle._id,
                updatedBundle.completionPercent ?? 0,
                updatedBundle.numAnswers ?? 0,
                updatedBundle.numControls ?? 0
              );
            }
          }

          // Note: Answer status context update is done BEFORE mutation (line 677-679)
          // to prevent race conditions with stale Apollo cache data

          // 3. Update assessment summary fields (completionPercent, status, updatedAt)
          const assessmentSummary = data?.assessmentUpdateAnswer?.assessment;
          if (assessmentSummary) {
            updateAssessmentInCache(cache, assessmentSummary);

            // Update progress context for live UI updates
            if (arg.progressContext && assessment?._id) {
              arg.progressContext.updateAssessmentCompletion(
                assessment._id,
                assessmentSummary.completionPercent ?? 0
              );
            }
          }
        },
      });
    } catch (err: any) {
      // Rollback context update on mutation failure
      // When previousStatus is undefined (first interaction), setting undefined
      // removes the override so the UI falls back to Apollo cache (pre-mutation state)
      if (arg.progressContext && contextKey) {
        arg.progressContext.updateAnswerStatus(
          contextKey,
          previousStatus as GQL.AnswerStatus | null
        );
      }
      // Revert optimistic left nav counts — left nav falls back to Apollo cache values
      if (arg.progressContext && answerBundleId) {
        arg.progressContext.removeBundleProgress(answerBundleId);
      }
      Snackbar.open(err);
      throw err; // Re-throw to let components handle UI rollback if needed
    }
  };

  /**
   * props that can be passed to input fields that are responsible for
   * altering answer values
   */
  const inputProps = (control: GQL.ControlFragment): InputCommonProps => {
    const controlId = control.identifier;
    const disabled = !canEditAssessment(assessment);
    // const readOnly = control.answerType === GQL.AnswerType.TestScore;
    const readOnly = false;
    return {
      control,
      readOnly,
      disabled,
      values: getAnswerValues(controlId),
      cards: getAnswerCards(controlId),
      answerStatus: getAnswerStatus(controlId),
      assignment: getAnswerAssignment(controlId),
      onValueChange: (values, answerStatus) => {
        if (readOnly || disabled) {
          return;
        }
        return onValueChange(controlId, values, answerStatus);
      },
      onCardOperation: (operation, card, isPartialUpdate) => {
        if (readOnly || disabled) {
          return;
        }
        return onCardOperation(
          controlId,
          operation,
          {
            _id: card._id?.toString(),
            cardId: card.cardId,
            values: card.values.map((v) => ({
              cardFieldId: v.cardFieldId,
              value: v.value ?? '',
              values: v?.values?.map((nestedV) => {
                if (!nestedV) {
                  return null;
                }
                const { __typename, ...others } = nestedV;
                return others;
              }),
            })),
          },
          isPartialUpdate
        );
      },
    };
  };

  /**
   * Helpers for paging control bundles
   */
  const isFirstBundle = controlBundleCount
    ? controlBundleList[0]._id === bundleId
    : false;
  const isLastBundle = controlBundleCount
    ? controlBundleList[controlBundleCount - 1]._id === bundleId
    : false;
  const nextBundleId =
    isLastBundle || bundleIdx === -1
      ? null
      : controlBundleList[bundleIdx + 1]._id;
  const prevBundleId =
    isFirstBundle || bundleIdx === -1
      ? null
      : controlBundleList[bundleIdx - 1]._id;
  const isProjectInfo =
    controlBundleList[controlBundleCount - 1]?.labels?.includes('project_info');

  const nextBundleName =
    isLastBundle || bundleIdx === -1
      ? null
      : controlBundleList[bundleIdx + 1].name;

  const prevBundleName =
    isFirstBundle || bundleIdx === -1
      ? null
      : controlBundleList[bundleIdx - 1].name;

  /* First unanswered control question Id */
  const getFirstUnansweredControl = () => {
    const unanswer = bundle?.controls.find((control) => {
      const values = getAnswerValues(control.identifier);
      return !values.some(({ value }) => !isEmpty(value));
    });
    return unanswer;
  };

  return {
    bundle,
    answerBundle,
    controlCount,
    answerCount,
    bundleCompletionPercent,
    inputProps,
    isFirstBundle,
    isLastBundle,
    nextBundleId,
    nextBundleName,
    prevBundleId,
    prevBundleName,
    isProjectInfo,
    getFirstUnansweredControl,
    organizedAssessmentByVersion,
    getAnswerValues,
  } as const;
}
